import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AddRevisionCategoryDialogProps {
  /** Controls open/close state */
  open: boolean;
  /** Called when the dialog should be closed (cancel or after successful add) */
  onOpenChange: (open: boolean) => void;
  /** Existing category labels — used to detect duplicates (case-insensitive) */
  existingLabels: string[];
  /** Called with the trimmed label when the user confirms */
  onAdd: (label: string) => void;
}

// ---------------------------------------------------------------------------
// Validation helpers
// ---------------------------------------------------------------------------

const MAX_LENGTH = 50;

function validate(value: string, existingLabels: string[]): string | null {
  const trimmed = value.trim();
  if (!trimmed) return 'Category name is required.';
  if (trimmed.length > MAX_LENGTH) return `Maximum ${MAX_LENGTH} characters allowed.`;
  const isDuplicate = existingLabels.some(
    (label) => label.trim().toLowerCase() === trimmed.toLowerCase(),
  );
  if (isDuplicate) return 'A category with this name already exists.';
  return null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AddRevisionCategoryDialog({
  open,
  onOpenChange,
  existingLabels,
  onAdd,
}: AddRevisionCategoryDialogProps) {
  const [value, setValue] = useState('');
  const [touched, setTouched] = useState(false);

  const error = touched ? validate(value, existingLabels) : null;
  const isValid = validate(value, existingLabels) === null;

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    if (!touched) setTouched(true);
  };

  const handleConfirm = () => {
    setTouched(true);
    if (!isValid) return;
    onAdd(value.trim());
    handleClose();
  };

  const handleClose = () => {
    setValue('');
    setTouched(false);
    onOpenChange(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleConfirm();
    if (e.key === 'Escape') handleClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Revision Category</DialogTitle>
          <DialogDescription>
            Add a custom category that PMs can select when creating a resource budget revision.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          <Label htmlFor="category-name">
            Category Name <span className="text-destructive">*</span>
          </Label>
          <Input
            id="category-name"
            placeholder="e.g. Client Request"
            value={value}
            onChange={handleValueChange}
            onKeyDown={handleKeyDown}
            maxLength={MAX_LENGTH}
            autoFocus
            className={error ? 'border-destructive focus-visible:ring-destructive' : ''}
            aria-describedby={error ? 'category-name-error' : undefined}
          />
          <div className="flex items-center justify-between min-h-[1.25rem]">
            {error ? (
              <p id="category-name-error" className="text-xs text-destructive">
                {error}
              </p>
            ) : (
              <span />
            )}
            <p className="text-xs text-muted-foreground ml-auto">
              {value.length}/{MAX_LENGTH}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={touched && !isValid}>
            Add Category
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
